import { Calculator } from "lucide-react";
import React, { useMemo } from "react";
import type {
	ComputedSourceRef,
	EvidenceEvent,
} from "../../../types/evidence";
import "./EvidenceCards.css";

interface CalculationCardProps {
	event: EvidenceEvent;
	/** All events from the same turn — used to label upstream chips. */
	allEvents: EvidenceEvent[];
	onUpstreamClick?: (eventId: string) => void;
}

interface UpstreamChipData {
	key: string;
	label: string;
	title: string;
	clickable: boolean;
	upstreamEventId: string | null;
}

interface DecoratedCalcRef {
	key: string;
	ref: ComputedSourceRef;
	chips: UpstreamChipData[];
}

const formatValue = (value: unknown): string => {
	if (value === null || value === undefined) return "—";
	if (typeof value === "number") return value.toLocaleString();
	if (typeof value === "object") return JSON.stringify(value);
	return String(value);
};

const decorateCalcRefs = (
	event: EvidenceEvent,
	eventsById: Map<string, EvidenceEvent>,
	canClick: boolean,
): DecoratedCalcRef[] =>
	event.source_refs
		.filter((r): r is ComputedSourceRef => r.kind === "computed")
		.map((ref, index) => ({
			key: `${ref.label}-${index}`,
			ref,
			chips: ref.inputs.map((input) => {
				const upstream = eventsById.get(input.upstream_event_id);
				return {
					key: input.upstream_event_id + input.name,
					label: `${input.name} = ${formatValue(input.value)}`,
					title: upstream
						? `${upstream.title} (#${upstream.sequence})`
						: "upstream event missing",
					clickable: canClick && upstream !== undefined,
					upstreamEventId: upstream ? upstream.event_id : null,
				};
			}),
		}));

const CalculationCard: React.FC<CalculationCardProps> = ({
	event,
	allEvents,
	onUpstreamClick,
}) => {
	const eventsById = useMemo(
		() => new Map(allEvents.map((e) => [e.event_id, e])),
		[allEvents],
	);
	const canClick = onUpstreamClick !== undefined;
	const decorated = useMemo(
		() => decorateCalcRefs(event, eventsById, canClick),
		[event, eventsById, canClick],
	);

	return (
		<article className="evidence-card">
			<header className="evidence-card-header">
				<Calculator className="evidence-card-header-icon" aria-hidden />
				<div className="evidence-card-header-body">
					<h3 className="evidence-card-title">{event.title}</h3>
					<p className="evidence-card-summary">{event.summary}</p>
				</div>
			</header>
			<ul className="evidence-card-list evidence-card-list--spaced">
				{decorated.map(({ key, ref, chips }) => (
					<li key={key} className="evidence-card-row">
						<div className="evidence-card-calc-label">{ref.label}</div>
						<div className="evidence-card-formula">{ref.formula}</div>
						{chips.length > 0 && (
							<div className="evidence-card-calc-inputs">
								{chips.map((chip) =>
									chip.clickable && chip.upstreamEventId ? (
										<button
											key={chip.key}
											type="button"
											onClick={() =>
												onUpstreamClick?.(chip.upstreamEventId as string)
											}
											title={chip.title}
											className="evidence-card-calc-chip evidence-card-calc-chip--clickable"
										>
											<span className="evidence-card-calc-chip-label">
												{chip.label}
											</span>
										</button>
									) : (
										<span
											key={chip.key}
											title={chip.title}
											className="evidence-card-calc-chip"
										>
											<span className="evidence-card-calc-chip-label">
												{chip.label}
											</span>
										</span>
									),
								)}
							</div>
						)}
					</li>
				))}
			</ul>
		</article>
	);
};

export default React.memo(CalculationCard);
